import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Header from './Header';
import Footer from './Footer';
import './MockInterviewPage.css';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import ProductSidebar from './ProductSidebar';
import { createDoc, PDF, fR, sR, T, drawCoverHeader, drawPageHeader, drawFooters, drawSectionHeading } from '../utils/pdfBrand';


// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const MockInterviewPage = () => {
  const navigate = useNavigate();
  const { user, loading, logout, credits } = useUser();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
  
  const [showJobForm, setShowJobForm] = useState(false);
  const [formData, setFormData] = useState({
    jobRole: '',
    experienceLevel: '',
    company: '',
    jobDescription: '',
    resumeText: ''
  });
  const [uploadedResumeFile, setUploadedResumeFile] = useState(null);
  const [uploadedJobDescFile, setUploadedJobDescFile] = useState(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [resumeChoice, setResumeChoice] = useState(null); // 'profile' | 'upload'
  const [fileUploadError, setFileUploadError] = useState('');
  
  // Mock test specific state
  const [mockTestStarted, setMockTestStarted] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isRecording, setIsRecording] = useState(false);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(45 * 60);
  const [recordingError, setRecordingError] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetch(`${API_BASE_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.profile) setProfileData(data.profile); })
        .catch(() => {});
    }
  }, [user, API_BASE_URL]);

  useEffect(() => {
    if (mockTestStarted && timeRemaining > 0) {
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleEndTest(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [mockTestStarted, timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getConfidenceMeta = (score) => {
    if (!score && score !== 0) return { color: '#888', label: 'N/A', cls: 'conf-na', gradFrom: '#888', gradTo: '#888' };
    if (score >= 70) return { color: '#4caf50', gradFrom: '#4caf50', gradTo: '#81c784', label: 'High Confidence', cls: 'conf-high' };
    if (score >= 40) return { color: '#ffc107', gradFrom: '#ffa000', gradTo: '#ffc107', label: 'Moderate Confidence', cls: 'conf-mid' };
    return { color: '#ff6b6b', gradFrom: '#f44336', gradTo: '#ff6b6b', label: 'Low Confidence', cls: 'conf-low' };
  };

  const downloadReport = () => {
    if (!evaluation) return;

    const doc = createDoc();
    const { W, H, M, CW } = PDF;
    let y = 0;

    const avgConfidence = evaluation.questionScores.length > 0
      ? Math.round(evaluation.questionScores.reduce((s, r) => s + (r.confidenceScore || 0), 0) / evaluation.questionScores.length)
      : 0;

    const sRgbFn = (s) => s >= 70 ? [22, 163, 74] : s >= 50 ? [202, 138, 4] : [220, 38, 38];
    const cRgbFn = (s) => (s || 0) >= 70 ? [22, 163, 74] : (s || 0) >= 40 ? [202, 138, 4] : [220, 38, 38];

    const addNewPage = () => {
      doc.addPage();
      fR(doc, 255, 255, 255, 0, 0, W, H);
      drawPageHeader(doc, formData.jobRole || 'Mock Interview Report');
      y = 21;
    };
    const checkBreak = (need) => { if (y + need > H - 16) addNewPage(); };

    const expStr = formData.experienceLevel
      ? formData.experienceLevel.charAt(0).toUpperCase() + formData.experienceLevel.slice(1) + ' Level'
      : '';
    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // Page 1 white bg + cover header
    fR(doc, 255, 255, 255, 0, 0, W, H);
    drawCoverHeader(doc, {
      reportTitle: 'Mock Interview Performance Report',
      role: formData.jobRole,
      company: formData.company,
      levelLabel: expStr,
      dateStr,
    });
    y = 62;

    // ── SCORE CARDS ──
    const cW = (CW - 8) / 3;
    [
      { label: 'Overall Score', value: `${evaluation.overallScore}%`, sub: evaluation.overallScore >= 70 ? 'Strong Performance' : evaluation.overallScore >= 50 ? 'Average' : 'Needs Improvement', rgb: sRgbFn(evaluation.overallScore) },
      { label: 'Avg Confidence', value: `${avgConfidence}%`, sub: avgConfidence >= 70 ? 'High Confidence' : avgConfidence >= 40 ? 'Moderate Confidence' : 'Low Confidence', rgb: cRgbFn(avgConfidence) },
      { label: 'Questions', value: `${evaluation.questionScores.length}`, sub: 'Total Answered', rgb: [79, 70, 229] },
    ].forEach((card, i) => {
      const cx = M + i * (cW + 4);
      fR(doc, 248, 247, 254, cx, y, cW, 30);
      sR(doc, 220, 215, 240, 0.4, cx, y, cW, 30);
      fR(doc, ...card.rgb, cx, y, cW, 3);
      T(doc, card.rgb, 19, true, card.value, cx + cW / 2, y + 17, { align: 'center' });
      T(doc, [55, 45, 75], 6.5, true, card.label.toUpperCase(), cx + cW / 2, y + 23, { align: 'center' });
      T(doc, [130, 120, 155], 6, false, card.sub, cx + cW / 2, y + 28, { align: 'center' });
    });
    y += 34;

    // ── READINESS BAR ──
    T(doc, [75, 65, 100], 7, true, 'INTERVIEW READINESS', M, y);
    T(doc, sRgbFn(evaluation.overallScore), 7, true, `${evaluation.overallScore}%`, M + CW, y, { align: 'right' });
    y += 4;
    fR(doc, 229, 224, 248, M, y, CW, 4.5);
    fR(doc, ...sRgbFn(evaluation.overallScore), M, y, CW * (evaluation.overallScore / 100), 4.5);
    y += 10;

    // ── GENERAL FEEDBACK ──
    const fbLines = doc.splitTextToSize(evaluation.generalFeedback || '', CW - 14);
    const fbH = fbLines.length * 5 + 14;
    checkBreak(fbH + 8);
    fR(doc, 248, 245, 255, M, y, CW, fbH);
    sR(doc, 196, 181, 253, 0.4, M, y, CW, fbH);
    fR(doc, 109, 40, 217, M, y, 3.5, fbH);
    T(doc, [76, 29, 149], 7.5, true, 'GENERAL FEEDBACK', M + 7, y + 6.5);
    T(doc, [45, 35, 65], 8, false, fbLines, M + 7, y + 12.5);
    y += fbH + 12;

    // ── SECTION HEADING ──
    checkBreak(14);
    y = drawSectionHeading(doc, `Question Breakdown  ·  ${evaluation.questionScores.length} questions evaluated`, y);

    // ── PER QUESTION ──
    evaluation.questionScores.forEach((result, index) => {
      const q = questions[index];
      const pace = result.pace || 0;
      const paceLabel = pace > 160 ? 'Fast' : pace > 110 ? 'Good' : pace > 0 ? 'Slow' : '—';
      const pRgb = pace > 160 ? [202, 138, 4] : pace > 110 ? [22, 163, 74] : pace > 0 ? [234, 88, 12] : [150, 140, 170];
      const fc = result.fillerWords || 0;
      const fcLabel = fc > 5 ? 'Too Many' : fc > 2 ? 'A Few' : 'Clean';
      const fcRgb = fc > 5 ? [220, 38, 38] : fc > 2 ? [202, 138, 4] : [22, 163, 74];
      const qsRgb = sRgbFn(result.score);
      const qcRgb = cRgbFn(result.confidenceScore);
      const cLabel = getConfidenceMeta(result.confidenceScore).label;

      const ansLinesAll = doc.splitTextToSize(q?.expectedAnswer || '', CW - 14);
      const ansShown = ansLinesAll.slice(0, 7);
      const advLinesAll = doc.splitTextToSize(result.advice || '', CW - 14);
      checkBreak(60);

      // Question header bar
      fR(doc, 22, 14, 52, M, y, CW, 11);
      fR(doc, ...qsRgb, M, y, 4, 11);
      T(doc, [196, 181, 253], 8, true, `Q${String(index + 1).padStart(2, '0')}`, M + 7, y + 7.5);
      const qShort = (q?.question || '').length > 90 ? (q?.question || '').slice(0, 87) + '…' : (q?.question || '');
      T(doc, [230, 225, 248], 8, false, qShort, M + 18, y + 7.5);
      T(doc, qsRgb, 8.5, true, `${result.score}/100`, M + CW - 3, y + 7.5, { align: 'right' });
      y += 13;

      // 4 metric boxes
      const mW = (CW - 6) / 4;
      [
        { label: 'Content Quality', val: `${result.contentScore}`, sub: '/50', rgb: [79, 70, 229] },
        { label: 'Authenticity',    val: `${result.authenticityScore}`, sub: '/50', rgb: [109, 40, 217] },
        { label: 'Speaking Pace',  val: pace > 0 ? `${pace}` : '—', sub: pace > 0 ? 'wpm' : '', tag: paceLabel, rgb: pRgb },
        { label: 'Filler Words',   val: `${fc}`, sub: '', tag: fcLabel, rgb: fcRgb },
      ].forEach((m, mi) => {
        const mx = M + mi * (mW + 2);
        fR(doc, 250, 249, 255, mx, y, mW, 21);
        sR(doc, 218, 213, 238, 0.3, mx, y, mW, 21);
        fR(doc, ...m.rgb, mx, y, mW, 2.5);
        T(doc, m.rgb, 12, true, m.val, mx + mW / 2, y + 12, { align: 'center' });
        if (m.sub) T(doc, [160, 150, 180], 6.5, false, m.sub, mx + mW / 2 + 5, y + 12);
        T(doc, [75, 65, 95], 6, true, m.label.toUpperCase(), mx + mW / 2, y + 16.5, { align: 'center' });
        if (m.tag) T(doc, m.rgb, 5.5, false, `(${m.tag})`, mx + mW / 2, y + 20, { align: 'center' });
      });
      y += 24;

      // Confidence row
      fR(doc, 248, 246, 255, M, y, CW, 11);
      sR(doc, 218, 213, 238, 0.3, M, y, CW, 11);
      T(doc, [90, 78, 118], 6.5, true, 'CONFIDENCE', M + 4, y + 4.5);
      const cbW = CW * 0.38;
      fR(doc, 220, 215, 240, M + 4, y + 6.2, cbW, 2.5);
      fR(doc, ...qcRgb, M + 4, y + 6.2, cbW * ((result.confidenceScore || 0) / 100), 2.5);
      T(doc, qcRgb, 7.5, true, `${result.confidenceScore ?? '—'}%`, M + cbW + 8, y + 4.5);
      T(doc, [100, 90, 130], 7, false, cLabel, M + cbW + 20, y + 4.5);
      const dColor = result.isReading ? [153, 60, 10] : [14, 100, 55];
      const dBg = result.isReading ? [255, 237, 213] : [209, 250, 229];
      const dBorder = result.isReading ? [253, 186, 116] : [110, 231, 183];
      fR(doc, ...dBg, M + CW - 40, y + 1.8, 38, 7.5);
      sR(doc, ...dBorder, 0.3, M + CW - 40, y + 1.8, 38, 7.5);
      T(doc, dColor, 6.5, true, result.isReading ? 'Reading Detected' : 'Authentic Delivery', M + CW - 21, y + 7, { align: 'center' });
      y += 14;

      // Model answer box
      checkBreak(ansShown.length * 5 + 14);
      const ansBoxH = ansShown.length * 5 + 13;
      fR(doc, 239, 246, 255, M, y, CW, ansBoxH);
      sR(doc, 147, 197, 253, 0.3, M, y, CW, ansBoxH);
      fR(doc, 59, 130, 246, M, y, 3.5, ansBoxH);
      T(doc, [30, 64, 175], 7, true, 'MODEL ANSWER', M + 7, y + 6);
      T(doc, [30, 58, 138], 8, false, ansShown, M + 7, y + 11.5);
      if (ansLinesAll.length > 7) T(doc, [100, 140, 210], 6.5, false, '… full answer in app', M + 7, y + ansBoxH - 2);
      y += ansBoxH + 5;

      // Advice box
      checkBreak(advLinesAll.length * 5 + 14);
      const advBoxH = advLinesAll.length * 5 + 13;
      fR(doc, 255, 252, 232, M, y, CW, advBoxH);
      sR(doc, 252, 211, 77, 0.3, M, y, CW, advBoxH);
      fR(doc, 245, 158, 11, M, y, 3.5, advBoxH);
      T(doc, [120, 53, 15], 7, true, 'IMPROVEMENT ADVICE', M + 7, y + 6);
      T(doc, [92, 50, 10], 8, false, advLinesAll, M + 7, y + 11.5);
      y += advBoxH + 12;
    });

    drawFooters(doc);

    const filename = `terview-${(formData.jobRole || 'interview').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
  };

  const buildResumeTextFromProfile = (profile) => {
    let text = '';
    if (profile.fullName) text += `${profile.fullName}\n`;
    if (profile.jobTitle) text += `${profile.jobTitle}\n`;
    if (profile.phone) text += `Phone: ${profile.phone}\n`;
    if (profile.location) text += `Location: ${profile.location}\n`;
    if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
    if (profile.github) text += `GitHub: ${profile.github}\n`;
    text += '\n';
    if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
    if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
    if (Array.isArray(profile.workExperience) && profile.workExperience.some(e => e.company || e.position)) {
      text += 'WORK EXPERIENCE\n';
      profile.workExperience.forEach(exp => {
        if (exp.position || exp.company) {
          text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
          if (exp.description) text += `${exp.description}\n`;
          text += '\n';
        }
      });
    }
    if (Array.isArray(profile.education) && profile.education.some(e => e.degree || e.institution)) {
      text += 'EDUCATION\n';
      profile.education.forEach(edu => {
        if (edu.degree || edu.institution) {
          text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
        }
      });
      text += '\n';
    }
    if (Array.isArray(profile.projects) && profile.projects.some(p => p.name)) {
      text += 'PROJECTS\n';
      profile.projects.forEach(proj => {
        if (proj.name) {
          text += `${proj.name}${proj.technologies ? ` (${proj.technologies})` : ''}\n`;
          if (proj.description) text += `${proj.description}\n`;
          text += '\n';
        }
      });
    }
    if (profile.certifications) text += `CERTIFICATIONS\n${profile.certifications}\n`;
    return text.trim();
  };

  // File processing functions
  const extractTextFromPDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }

      return fullText.trim();
    } catch (error) {
      console.error('Error parsing PDF:', error);
      throw new Error('Failed to parse PDF file. Please ensure the PDF contains readable text.');
    }
  };

  const extractTextFromDOCX = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      console.error('Error parsing DOCX:', error);
      throw new Error('Failed to parse DOCX file. Please try pasting the text manually.');
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsProcessingFile(true);
    setFileUploadError('');

    try {
      if (type === 'resume') {
        setUploadedResumeFile(file);
      } else if (type === 'jobDesc') {
        setUploadedJobDescFile(file);
      }

      let extractedText = '';

      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        const reader = new FileReader();
        extractedText = await new Promise((resolve, reject) => {
          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = (error) => reject(error);
          reader.readAsText(file);
        });
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        extractedText = await extractTextFromPDF(file);
      } else if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.name.endsWith('.docx')
      ) {
        extractedText = await extractTextFromDOCX(file);
      } else {
        throw new Error('Unsupported file format. Please upload .txt, .pdf, or .docx files.');
      }

      extractedText = extractedText.trim();

      if (!extractedText) {
        throw new Error('No text could be extracted from the file.');
      }

      if (type === 'resume') {
        setFormData(prevData => ({
          ...prevData,
          resumeText: extractedText
        }));
        setFileUploadError('✓ Resume uploaded successfully!');
      } else if (type === 'jobDesc') {
        setFormData(prevData => ({
          ...prevData,
          jobDescription: extractedText
        }));
        setFileUploadError('✓ Job description uploaded successfully!');
      }

      setTimeout(() => {
        setFileUploadError('');
      }, 3000);

    } catch (error) {
      console.error('Error processing file:', error);
      setFileUploadError(error.message || 'Failed to process file.');
      
      if (type === 'resume') {
        setUploadedResumeFile(null);
      } else if (type === 'jobDesc') {
        setUploadedJobDescFile(null);
      }
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleStartMockTest = async () => {
    if (formData.jobRole && formData.experienceLevel && formData.jobDescription && formData.resumeText) {
      try {
        setIsGeneratingQuestions(true);
        
        // Deduct credit
        const response = await fetch(`${API_BASE_URL}/api/mock-interview/deduct-credit`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const data = await response.json();
          if (response.status === 403) {
            setShowJobForm(false);
            setShowInsufficientCreditsModal(true);
            setIsGeneratingQuestions(false);
            return;
          }
          throw new Error(data.error || 'Failed to deduct credit');
        }

        // Generate 15 questions
        const questionsResponse = await fetch(`${API_BASE_URL}/api/mock-interview/generate-questions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            jobRole: formData.jobRole,
            experienceLevel: formData.experienceLevel,
            company: formData.company,
            jobDescription: formData.jobDescription,
            resumeText: formData.resumeText
          })
        });

        if (!questionsResponse.ok) {
          throw new Error('Failed to generate questions');
        }

        const questionsData = await questionsResponse.json();
        console.log('🔍 First question data:', questionsData.questions[0]);
        setQuestions(questionsData.questions);
        setMockTestStarted(true);
        setShowJobForm(false);
        setTimeRemaining((credits >= 999000) ? 90 * 60 : 45 * 60);
      } catch (error) {
        alert(error.message || 'Failed to start mock test. Please try again.');
      } finally {
        setIsGeneratingQuestions(false);
      }
    } else {
      alert('Please complete all required fields before starting the mock test.');
    }
  };

  const startRecording = async (questionIndex) => {
    try {
      setRecordingError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Save the audio for this question
        setAnswers(prev => ({
          ...prev,
          [questionIndex]: {
            audio: audioBlob,
            recordedAt: new Date().toISOString()
          }
        }));
        
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      setRecordingError('Failed to start recording. Please check microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const deleteRecording = (questionIndex) => {
    setAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[questionIndex];
      return newAnswers;
    });
  };

  const handleSubmitTest = async () => {
    const unansweredCount = questions.length - Object.keys(answers).length;
    
    if (unansweredCount > 0) {
      const confirm = window.confirm(
        `You have ${unansweredCount} unanswered question(s). Do you want to submit anyway?`
      );
      if (!confirm) return;
    }

    try {
      setIsEvaluating(true);
      
      // Prepare form data with audio files
      const formDataToSend = new FormData();
      formDataToSend.append('jobRole', formData.jobRole);
      formDataToSend.append('company', formData.company || '');
      formDataToSend.append('experienceLevel', formData.experienceLevel);
      formDataToSend.append('jobDescription', formData.jobDescription);
      formDataToSend.append('resumeText', formData.resumeText);
      formDataToSend.append('questions', JSON.stringify(questions));
      formDataToSend.append('duration', 45 * 60 - timeRemaining);
      
      // Add audio files
      Object.entries(answers).forEach(([questionIndex, answer]) => {
        formDataToSend.append(`audio_${questionIndex}`, answer.audio, `answer_${questionIndex}.webm`);
      });

      const response = await fetch(`${API_BASE_URL}/api/mock-interview/evaluate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error('Failed to evaluate test');
      }

      const evaluationData = await response.json();
      setEvaluation(evaluationData);
      
      // Stop timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      alert('Failed to evaluate test. Please try again.');
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleEndTest = async (isAutoEnd = false) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    if (isAutoEnd) {
      alert('Time is up! Your test will now be evaluated.');
      await handleSubmitTest();
    } else {
      const confirm = window.confirm('Are you sure you want to end the test?');
      if (confirm) {
        navigate('/dashboard');
      }
    }
  };

  const handleHeaderNavigation = async (path) => {
    if (mockTestStarted && !evaluation) {
      const confirmEnd = window.confirm(
        'Mock test is in progress. Do you want to end the test? Your progress will be lost.'
      );
      if (!confirmEnd) return;
      
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }

    if (path === '/logout-redirect') {
      try {
        await fetch(`${API_BASE_URL}/api/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ email: user?.email })
        });
      } catch (error) {
        console.error('Logout API error:', error);
      }
      
      logout();
      navigate('/');
    } else {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <div className="neural-glass-homepage">
        <div className="neural-background"></div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          color: '#fff',
          fontSize: '1.2rem'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="neural-glass-homepage">
      <div className="neural-background"></div>
      
      <div className="geometric-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      {showJobForm && (
        <div className="job-form-popup-overlay">
          <div className="job-form-popup glass">
            <div className="popup-header">
              <h3>Enter Job Information</h3>
              <button 
                className="close-popup" 
                onClick={() => setShowJobForm(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleStartMockTest();
            }}>
              <div className="form-group">
                <input
                  type="text"
                  name="jobRole"
                  placeholder="Job Role/Position *"
                  value={formData.jobRole}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <select
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Experience Level *</option>
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                  <option value="executive">Executive</option>
                </select>
              </div>
              
              <div className="form-group">
                <input
                  type="text"
                  name="company"
                  placeholder="Company (optional)"
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group form-group-ip">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ color: '#e0a3ff', fontWeight: '500' }}>Job Description *</label>
                  {uploadedJobDescFile && (
                    <div style={{ 
                      fontSize: '0.85rem', 
                      color: '#9370db', 
                      padding: '6px',
                      background: 'rgba(147, 112, 219, 0.1)',
                      borderRadius: '4px'
                    }}>
                      📄 {uploadedJobDescFile.name}
                    </div>
                  )}
                  <label htmlFor="job-desc-upload" style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: isProcessingFile ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem',
                    opacity: isProcessingFile ? 0.6 : 1
                  }}>
                    {isProcessingFile ? 'Processing...' : '📄 Upload File'}
                  </label>
                  <input
                    id="job-desc-upload"
                    type="file"
                    accept=".txt,.doc,.docx,.pdf"
                    onChange={(e) => handleFileUpload(e, 'jobDesc')}
                    disabled={isProcessingFile}
                    style={{ display: 'none' }}
                  />
                </div>
                <textarea
                  name="jobDescription"
                  placeholder="Paste job description or upload a file"
                  value={formData.jobDescription}
                  onChange={handleInputChange}
                  rows="5"
                  required
                  disabled={isProcessingFile}
                ></textarea>
              </div>
              
              <div className="form-group form-group-ip">
                <label style={{ color: '#e0a3ff', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Your Resume/CV *</label>
                {profileData && (profileData.professionalSummary || profileData.skills || (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position))) ? (
                  <>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setResumeChoice('profile');
                          setFormData(prev => ({ ...prev, resumeText: buildResumeTextFromProfile(profileData) }));
                          setUploadedResumeFile(null);
                        }}
                        style={{
                          flex: 1, padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                          border: `2px solid ${resumeChoice === 'profile' ? '#9370db' : 'rgba(255,255,255,0.2)'}`,
                          background: resumeChoice === 'profile' ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.05)',
                          color: 'white', fontSize: '0.85rem', transition: 'all 0.2s', textAlign: 'left'
                        }}
                      >
                        <div style={{ fontWeight: '600' }}>✅ Use Profile Resume</div>
                        {profileData.fullName && <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '3px' }}>{profileData.fullName}</div>}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setResumeChoice('upload');
                          setFormData(prev => ({ ...prev, resumeText: '' }));
                          setUploadedResumeFile(null);
                        }}
                        style={{
                          flex: 1, padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                          border: `2px solid ${resumeChoice === 'upload' ? '#9370db' : 'rgba(255,255,255,0.2)'}`,
                          background: resumeChoice === 'upload' ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.05)',
                          color: 'white', fontSize: '0.85rem', transition: 'all 0.2s', textAlign: 'left'
                        }}
                      >
                        <div style={{ fontWeight: '600' }}>📄 Upload New Resume</div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '3px' }}>Choose a different file</div>
                      </button>
                    </div>
                    {resumeChoice === 'profile' && (
                      <div style={{
                        padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem',
                        background: 'rgba(147,112,219,0.1)', border: '1px solid rgba(147,112,219,0.3)', color: '#e0a3ff'
                      }}>
                        ✓ Your profile resume will be used for this mock interview session.
                      </div>
                    )}
                    {resumeChoice === 'upload' && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                          {uploadedResumeFile && (
                            <div style={{ fontSize: '0.85rem', color: '#9370db', padding: '6px', background: 'rgba(147,112,219,0.1)', borderRadius: '4px' }}>
                              📄 {uploadedResumeFile.name}
                            </div>
                          )}
                          <label htmlFor="resume-upload" style={{
                            padding: '6px 12px', background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
                            color: 'white', borderRadius: '6px', cursor: isProcessingFile ? 'not-allowed' : 'pointer',
                            fontSize: '0.85rem', opacity: isProcessingFile ? 0.6 : 1
                          }}>
                            {isProcessingFile ? 'Processing...' : '📄 Upload Resume'}
                          </label>
                          <input id="resume-upload" type="file" accept=".txt,.doc,.docx,.pdf"
                            onChange={(e) => handleFileUpload(e, 'resume')} disabled={isProcessingFile} style={{ display: 'none' }} />
                        </div>
                        <textarea name="resumeText" placeholder="Paste your resume content or upload a file"
                          value={formData.resumeText} onChange={handleInputChange} rows="8" required disabled={isProcessingFile}></textarea>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      {uploadedResumeFile && (
                        <div style={{ fontSize: '0.85rem', color: '#9370db', padding: '6px', background: 'rgba(147,112,219,0.1)', borderRadius: '4px' }}>
                          📄 {uploadedResumeFile.name}
                        </div>
                      )}
                      <label htmlFor="resume-upload" style={{
                        padding: '6px 12px', background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
                        color: 'white', borderRadius: '6px', cursor: isProcessingFile ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem', opacity: isProcessingFile ? 0.6 : 1, marginLeft: 'auto'
                      }}>
                        {isProcessingFile ? 'Processing...' : '📄 Upload Resume'}
                      </label>
                      <input id="resume-upload" type="file" accept=".txt,.doc,.docx,.pdf"
                        onChange={(e) => handleFileUpload(e, 'resume')} disabled={isProcessingFile} style={{ display: 'none' }} />
                    </div>
                    <textarea name="resumeText" placeholder="Paste your resume content or upload a file"
                      value={formData.resumeText} onChange={handleInputChange} rows="8" required disabled={isProcessingFile}></textarea>
                  </>
                )}
              </div>
              
              {fileUploadError && (
                <div style={{
                  padding: '10px',
                  marginBottom: '15px',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  background: fileUploadError.startsWith('✓') 
                    ? 'rgba(76, 175, 80, 0.1)' 
                    : 'rgba(244, 67, 54, 0.1)',
                  color: fileUploadError.startsWith('✓') ? '#4caf50' : '#f44336',
                  border: `1px solid ${fileUploadError.startsWith('✓') ? '#4caf50' : '#f44336'}`
                }}>
                  {fileUploadError}
                </div>
              )}
              
              <button 
                type="submit" 
                className="cta-button" 
                disabled={isProcessingFile || isGeneratingQuestions}
              >
                {isGeneratingQuestions ? 'Generating Questions...' : 'Start Mock Test (1 Credit)'}
              </button>
            </form>
          </div>
        </div>
      )}
      
      <div className="mock-interview-page-container">
        {showInsufficientCreditsModal && (
          <div className="modal-overlay" onClick={() => setShowInsufficientCreditsModal(false)}>
            <div className="modal-content glass" onClick={(e) => e.stopPropagation()}>
              <h3>Insufficient Credits</h3>
              <p>You need at least 1 credit to start a mock interview.</p>
              <p>Current credits: <strong>{credits}</strong></p>
              <div className="modal-actions">
                <button className="modal-btn primary" onClick={() => {
                  setShowInsufficientCreditsModal(false);
                  document.dispatchEvent(new CustomEvent('openPricingModal'));
                }}>
                  Get More Credits
                </button>
                <button className="modal-btn" onClick={() => setShowInsufficientCreditsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <Header 
          onNavigate={handleHeaderNavigation}
          interviewInProgress={mockTestStarted && !evaluation}
        />
        <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

        <main className="mock-interview-main">
          <div className="li-hero">
            <div className="li-hero-badge">🚀 AI-Powered</div>
            <h1 className="li-hero-title">
              AI Mock<span className="li-gradient-text"> Interview</span>
            </h1>
            <p className="li-hero-subtitle">
              Practice smarter with AI mock interviews designed to sharpen your interview skills.
            </p>
          </div>
          {!mockTestStarted ? (
            <div className="mock-test-intro">
              <div className="">
                <p className="intro-subtitle">
                  Practice with 15 AI-generated interview questions tailored to your role
                </p>
                <button
                  onClick={() => {
                    if (credits < 1) {
                      setShowInsufficientCreditsModal(true);
                      return;
                    }
                    const hasProfileResume = profileData && (
                      profileData.professionalSummary ||
                      profileData.skills ||
                      (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position))
                    );
                    if (hasProfileResume && !resumeChoice) {
                      setResumeChoice('profile');
                      setFormData(prev => ({ ...prev, resumeText: buildResumeTextFromProfile(profileData) }));
                    } else if (!hasProfileResume && !resumeChoice) {
                      setResumeChoice('upload');
                    }
                    setShowJobForm(true);
                  }}
                  className="start-mock-test-btn"
                >
                  Start Mock Test
                </button>
              </div>

            {/* Why Use TERVIEW Mock Test */}
            <section className="info-section glass">
              <h2 className="info-section-title">Why Use TERVIEW Mock Interview?</h2>
              <div className="info-grid">
                <div className="info-card glass">
                  <div className="info-icon">🎯</div>
                  <h3>AI-Tailored Questions</h3>
                  <p>Every question is generated specifically for your job role, experience level, and resume. No generic question banks — every session is unique to your career profile.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">🎙️</div>
                  <h3>Speak & Get Scored</h3>
                  <p>Record your answers out loud just like a real interview. Our AI evaluates your spoken delivery, content coverage, and clarity — giving you a real readiness score.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">📊</div>
                  <h3>Detailed Feedback</h3>
                  <p>Get per-question scores with actionable advice on what you covered well and what to improve. Track your progress across sessions in your dashboard.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">🧠</div>
                  <h3>Learn Model Answers</h3>
                  <p>Each question comes with a full model answer written by AI. Read it, internalize it, and record yourself saying it — the best way to absorb interview-ready language.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">⏱️</div>
                  <h3>Timed Practice</h3>
                  <p>Practice under real interview pressure with a countdown timer. Build the stamina and focus needed to answer 15 questions confidently in a single sitting.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">🔒</div>
                  <h3>Safe Environment</h3>
                  <p>Make mistakes here, not in the real interview. Practice as many times as you need until your answers feel natural, confident, and polished.</p>
                </div>
              </div>
            </section>

            {/* How It Works */}
            <section className="how-it-works-section glass">
              <h2 className="info-section-title">How It Works</h2>
              <div className="steps-grid">
                <div className="step-card glass">
                  <div className="step-number">1</div>
                  <h4>Enter Job Details</h4>
                  <p>Provide your target job role, experience level, company name, and paste or upload the job description. The AI uses all of this to craft relevant questions.</p>
                </div>
                <div className="step-card glass">
                  <div className="step-number">2</div>
                  <h4>Upload Your Resume</h4>
                  <p>Upload your resume (PDF, DOCX, or TXT). The AI reads your background and tailors both the questions and model answers to your actual experience.</p>
                </div>
                <div className="step-card glass">
                  <div className="step-number">3</div>
                  <h4>Read the Model Answer</h4>
                  <p>Each question displays a full AI-generated model answer. Read it carefully so you understand what a great response looks like before recording.</p>
                </div>
                <div className="step-card glass">
                  <div className="step-number">4</div>
                  <h4>Record Your Answer</h4>
                  <p>Hit Record and speak the answer aloud. Our system uses Whisper AI to transcribe your speech accurately, even with technical jargon and proper nouns.</p>
                </div>
                <div className="step-card glass">
                  <div className="step-number">5</div>
                  <h4>Get Your Evaluation</h4>
                  <p>Submit the test and receive a detailed evaluation with an overall score, per-question breakdown, content quality scores, delivery ratings, and improvement advice.</p>
                </div>
              </div>
            </section>

            {/* Tips Section */}
            <section className="tips-section glass">
              <h2 className="info-section-title">Mock Interview Tips</h2>
              <div className="tips-columns">
                <div className="tip-column glass">
                  <h4>🎯 Before You Start</h4>
                  <ul>
                    <li>Read the job description carefully and note key skills required</li>
                    <li>Have an updated, detailed resume ready to upload</li>
                    <li>Find a quiet room with no background noise for clear recording</li>
                    <li>Test your microphone before starting the session</li>
                    <li>Set aside at least 45–60 uninterrupted minutes</li>
                    <li>Have a glass of water nearby — speaking 15 answers is a workout</li>
                  </ul>
                </div>
                <div className="tip-column glass">
                  <h4>🎙️ During the Test</h4>
                  <ul>
                    <li>Read the model answer fully before hitting Record</li>
                    <li>Speak clearly and at a natural conversational pace</li>
                    <li>Don't rush — quality delivery scores higher than speed</li>
                    <li>If you stumble, stop recording and re-record the answer</li>
                    <li>Use the question navigator to skip and return to hard questions</li>
                    <li>Watch the timer — aim to answer each question in 2–3 minutes</li>
                  </ul>
                </div>
                <div className="tip-column glass">
                  <h4>📈 After Your Results</h4>
                  <ul>
                    <li>Review every question where you scored below 70 carefully</li>
                    <li>Re-read the model answers for low-scoring questions</li>
                    <li>Take notes on the AI advice for each question</li>
                    <li>Retake the test for the same role to track improvement</li>
                    <li>Practice the hardest questions daily until they feel natural</li>
                    <li>Use your scores to identify weak topic areas to study further</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Benefits Banner */}
            <section className="info-section glass">
              <h2 className="info-section-title">What You Get With Every Mock Test</h2>
              <div className="info-grid">
                <div className="info-card glass">
                  <div className="info-icon">✅</div>
                  <h3>15 Personalized Questions</h3>
                  <p>A mix of behavioral, technical, situational, and resume-based questions — covering every angle an interviewer might take.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">📝</div>
                  <h3>Full Model Answers</h3>
                  <p>Every question includes a complete, first-person model answer written specifically for your role and experience level.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">🏆</div>
                  <h3>Overall Readiness Score</h3>
                  <p>Walk away knowing exactly where you stand — and what to work on before your real interview day.</p>
                </div>
              </div>
            </section>

          </div>
          ) : evaluation ? (
            <div className="evaluation-results">
              {(() => {
                const avgConfidence = evaluation.questionScores.length > 0
                  ? Math.round(evaluation.questionScores.reduce((s, r) => s + (r.confidenceScore || 0), 0) / evaluation.questionScores.length)
                  : 0;
                const answeredCount = evaluation.questionScores.filter(r => r.score > 0 || r.contentScore > 0).length;
                const confMeta = getConfidenceMeta(avgConfidence);
                return (
                  <>
                    <div className="results-header glass">
                      <h2>Mock Interview Results</h2>
                      <div className="results-summary-row">
                        <div className="summary-stat">
                          <span className="summary-stat-label">Overall Score</span>
                          <span className="summary-stat-value score-gradient">{evaluation.overallScore}%</span>
                        </div>
                        <div className="summary-stat-divider" />
                        <div className="summary-stat">
                          <span className="summary-stat-label">Avg Confidence</span>
                          <span className={`summary-stat-value ${confMeta.cls}`}>{avgConfidence}%</span>
                        </div>
                        <div className="summary-stat-divider" />
                        <div className="summary-stat">
                          <span className="summary-stat-label">Answered</span>
                          <span className="summary-stat-value">{answeredCount}/{evaluation.questionScores.length}</span>
                        </div>
                      </div>
                      <div className="overall-confidence-bar-wrap">
                        <div className="overall-confidence-bar-track">
                          <div className="overall-confidence-bar-fill" style={{ width: `${evaluation.overallScore}%` }} />
                        </div>
                        <span className="overall-bar-label">Readiness Score</span>
                      </div>
                      <div className="results-header-actions">
                        <button onClick={downloadReport} className="download-report-btn">
                          ⬇ Download PDF Report
                        </button>
                        <button onClick={() => navigate('/dashboard')} className="cta-button results-dash-btn">
                          Back to Dashboard
                        </button>
                      </div>
                    </div>

                    <div className="general-feedback glass">
                      <h3>General Feedback</h3>
                      <p>{evaluation.generalFeedback}</p>
                    </div>

                    <div className="questions-evaluation">
                      {evaluation.questionScores.map((result, index) => {
                        const cm = getConfidenceMeta(result.confidenceScore);
                        const fillerCount = result.fillerWords || 0;
                        const pace = result.pace || 0;
                        const paceLabel = pace > 160 ? 'Fast' : pace > 110 ? 'Good' : pace > 0 ? 'Slow' : '—';
                        const paceCls = pace > 160 ? 'pace-fast' : pace > 110 ? 'pace-good' : pace > 0 ? 'pace-slow' : '';
                        return (
                          <div key={index} className="question-result glass">
                            <div className="question-header">
                              <div className="qr-title-row">
                                <span className="qr-num">Q{index + 1}</span>
                                <span className="qr-question-text">{questions[index]?.question}</span>
                              </div>
                              <span className={`score-badge ${result.score >= 70 ? 'good' : result.score >= 50 ? 'medium' : 'low'}`}>
                                {result.score}/100
                              </span>
                            </div>

                            <div className="expected-answer">
                              <strong>Model Answer</strong>
                              <p>{questions[index]?.expectedAnswer}</p>
                            </div>

                            <div className="metrics-grid">
                              <div className="metric-box">
                                <span className="metric-label">Content</span>
                                <span className="metric-value">{result.contentScore}<span className="metric-max">/50</span></span>
                                <div className="metric-mini-bar">
                                  <div className="metric-mini-fill" style={{ width: `${(result.contentScore / 50) * 100}%`, background: '#667eea' }} />
                                </div>
                              </div>
                              <div className="metric-box">
                                <span className="metric-label">Authenticity</span>
                                <span className="metric-value">{result.authenticityScore}<span className="metric-max">/50</span></span>
                                <div className="metric-mini-bar">
                                  <div className="metric-mini-fill" style={{ width: `${(result.authenticityScore / 50) * 100}%`, background: '#e0a3ff' }} />
                                </div>
                              </div>
                              <div className="metric-box">
                                <span className="metric-label">Speaking Pace</span>
                                <span className={`metric-value ${paceCls}`}>{pace > 0 ? pace : '—'}<span className="metric-max">{pace > 0 ? ' wpm' : ''}</span></span>
                                {pace > 0 && <span className={`pace-tag ${paceCls}`}>{paceLabel}</span>}
                              </div>
                              <div className="metric-box">
                                <span className="metric-label">Filler Words</span>
                                <span className={`metric-value ${fillerCount > 5 ? 'metric-warn' : fillerCount > 2 ? 'metric-caution' : 'metric-ok'}`}>{fillerCount}</span>
                                <span className={`filler-tag ${fillerCount > 5 ? 'filler-high' : fillerCount > 2 ? 'filler-mid' : 'filler-low'}`}>
                                  {fillerCount > 5 ? 'Too Many' : fillerCount > 2 ? 'A Few' : 'Clean'}
                                </span>
                              </div>
                            </div>

                            <div className="confidence-section">
                              <div className="confidence-header-row">
                                <span className="confidence-label">Confidence Level</span>
                                <div className="confidence-right">
                                  <span className={`confidence-pct ${cm.cls}`}>{result.confidenceScore ?? '—'}%</span>
                                  <span className={`confidence-tag ${cm.cls}`}>{cm.label}</span>
                                  <span className={`delivery-badge ${result.isReading ? 'badge-reading' : 'badge-authentic'}`}>
                                    {result.isReading ? '📖 Reading Detected' : '🎤 Authentic Delivery'}
                                  </span>
                                </div>
                              </div>
                              <div className="confidence-bar-track">
                                <div
                                  className="confidence-bar-fill"
                                  style={{
                                    width: `${result.confidenceScore ?? 0}%`,
                                    background: `linear-gradient(90deg, ${cm.gradFrom}, ${cm.gradTo})`
                                  }}
                                />
                              </div>
                            </div>

                            <div className="improvement-advice">
                              <strong>Advice</strong>
                              <p>{result.advice}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                  </>
                );
              })()}
            </div>
          ) : (
            <div className="mock-test-interface">
              <div className="test-header glass">
                <div className="test-info">
                  <h2>{formData.jobRole} - Mock Interview</h2>
                  <p>Question {currentQuestionIndex + 1} of {questions.length}</p>
                </div>
                <div className="test-controls">
                  <div className="timer" style={{
                    padding: '8px 16px',
                    background: timeRemaining < 300 ? '#ff6b6b' : '#9370db',
                    borderRadius: '8px',
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    {formatTime(timeRemaining)}
                  </div>
                  <button onClick={() => handleEndTest(false)} className="end-button">
                    End Test
                  </button>
                </div>
              </div>

              <div className="question-panel glass">
                <div className="question-content">
                  <h3>Question {currentQuestionIndex + 1}</h3>
                  <p className="question-text">{questions[currentQuestionIndex]?.question}</p>

                  <div style={{
                    marginTop: '20px',
                    background: 'rgba(147, 112, 219, 0.12)',
                    border: '1px solid rgba(147, 112, 219, 0.4)',
                    borderLeft: '4px solid #9370db',
                    borderRadius: '10px',
                    padding: '18px 20px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '1.1rem' }}>📖</span>
                      <strong style={{ color: '#e0a3ff', fontSize: '1rem' }}>
                        Read & Record This Answer:
                      </strong>
                    </div>
                    <p style={{
                      color: '#f0e0ff',
                      lineHeight: '1.9',
                      fontSize: '1rem',
                      whiteSpace: 'pre-wrap',
                      margin: 0
                    }}>
                      {questions[currentQuestionIndex]?.expectedAnswer 
                        || questions[currentQuestionIndex]?.answer
                        || questions[currentQuestionIndex]?.sampleAnswer
                        || questions[currentQuestionIndex]?.ideal_answer
                        || 'Answer will appear here. Check your API response includes an answer field.'}
                    </p>
                    <p style={{
                      fontSize: '0.78rem',
                      color: 'rgba(255,255,255,0.45)',
                      marginTop: '14px',
                      marginBottom: 0,
                      fontStyle: 'italic'
                    }}>
                      🎤 Read the answer above out loud, then hit Record below.
                    </p>
                  </div>
                </div>

                <div className="recording-section">
                  {recordingError && (
                    <div className="recording-error">{recordingError}</div>
                  )}
                  
                  {answers[currentQuestionIndex] ? (
                    <div className="recorded-answer">
                      <div className="recorded-indicator">
                        <span className="check-icon">✓</span>
                        <span>Answer Recorded</span>
                      </div>
                      <button 
                        onClick={() => deleteRecording(currentQuestionIndex)}
                        className="delete-recording-btn"
                      >
                        Re-record Answer
                      </button>
                    </div>
                  ) : (
                    <div className="recording-controls">
                      {!isRecording ? (
                        <button 
                          onClick={() => startRecording(currentQuestionIndex)}
                          className="record-btn"
                        >
                          <span className="record-icon">🎤</span>
                          Record Answer
                        </button>
                      ) : (
                        <button 
                          onClick={stopRecording}
                          className="stop-recording-btn recording-active"
                        >
                          <span className="recording-pulse"></span>
                          Stop Recording
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="navigation-controls">
                <button 
                  onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentQuestionIndex === 0}
                  className="nav-btn"
                >
                  ← Previous
                </button>
                
                <div className="question-indicators">
                  {questions.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentQuestionIndex(index)}
                      className={`question-indicator ${
                        index === currentQuestionIndex ? 'active' : ''
                      } ${answers[index] ? 'answered' : ''}`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                
                {currentQuestionIndex < questions.length - 1 ? (
                  <button 
                    onClick={() => setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1))}
                    className="nav-btn"
                  >
                    Next →
                  </button>
                ) : (
                  <button 
                    onClick={handleSubmitTest}
                    disabled={isEvaluating}
                    className="submit-btn test-btn"
                  >
                    {isEvaluating ? 'Evaluating...' : 'Submit Test'}
                  </button>
                )}
              </div>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default MockInterviewPage;